<div *ngIf="isLoading()" class="flex flex-col items-center justify-center py-20 gap-4">
  <i class="pi pi-spinner pi-spin text-4xl text-primary"></i>
  <p class="text-sm font-bold text-slate-500 tracking-tight">{{ 'common_loading' | translate }}...</p>
</div>

<form *ngIf="!isLoading()" [formGroup]="bookingForm" (ngSubmit)="onSubmit()"
  class="flex flex-col animate-fadeIn w-full">

  <div class="p-6">
    <fieldset [disabled]="!canEdit" class="flex flex-col gap-10 max-w-2xl">

      <div class="flex flex-col gap-1">
        <h3 class="text-xl font-bold text-slate-700 tracking-tight">{{ 'booking_settings_title' | translate }}</h3>
        <p class="text-sm text-slate-400 font-medium">{{ 'booking_settings_subtitle' | translate }}</p>
      </div>

      <!-- Booking mode -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-semibold text-slate-500">
          {{ 'booking_settings_mode_label' | translate }}
        </label>
        <div class="flex flex-col gap-3">
          <div *ngFor="let opt of bookingModeOptions" class="flex items-center gap-2">
            <p-radioButton
              name="booking_mode"
              [value]="opt.value"
              formControlName="booking_mode"
              [inputId]="'bookingMode_' + opt.value">
            </p-radioButton>
            <label [for]="'bookingMode_' + opt.value" class="text-sm text-slate-600 cursor-pointer select-none">
              {{ opt.label | translate }}
            </label>
          </div>
        </div>
      </div>

      <!-- Doctor selection mode -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-semibold text-slate-500">
          {{ 'booking_settings_doctor_selection_label' | translate }}
        </label>
        <div class="flex flex-col gap-3">
          <div *ngFor="let opt of doctorSelectionOptions" class="flex items-center gap-2">
            <p-radioButton
              name="doctor_selection_mode"
              [value]="opt.value"
              formControlName="doctor_selection_mode"
              [inputId]="'doctorSelectionMode_' + opt.value">
            </p-radioButton>
            <label [for]="'doctorSelectionMode_' + opt.value" class="text-sm text-slate-600 cursor-pointer select-none">
              {{ opt.label | translate }}
            </label>
          </div>
        </div>

        <!-- Doctor picker, only shown/required when doctor_selection_mode = 'selected' -->
        <div *ngIf="bookingForm.value.doctor_selection_mode === 'selected'" class="flex flex-col gap-2 pt-2">
          <p-multiSelect
            formControlName="doctor_ids"
            [options]="doctorOptions()"
            optionLabel="name"
            optionValue="id"
            display="chip"
            [filter]="true"
            filterBy="name"
            appendTo="body"
            [placeholder]="'booking_settings_doctor_ids_placeholder' | translate"
            styleClass="!w-full !rounded-xl !border-slate-200 !text-sm">
          </p-multiSelect>
          <span *ngIf="bookingForm.get('doctor_ids')?.invalid && bookingForm.get('doctor_ids')?.touched"
            class="text-xs text-rose-500 font-medium">
            {{ 'booking_settings_doctor_ids_error' | translate }}
          </span>
        </div>
      </div>

      <!-- Booking window -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-semibold text-slate-500">
          {{ 'booking_settings_window_label' | translate }}<span class="text-rose-500 ml-0.5">*</span>
        </label>
        <input type="number" pInputText formControlName="booking_window_days" min="1" max="365"
          class="!w-full !max-w-[200px] !rounded-xl !border-slate-200 !px-4 !py-3 !text-sm focus:!border-primary transition-all shadow-none h-[48px]">
        <div class="flex items-center gap-2 pt-1">
          <button type="button" *ngFor="let preset of bookingWindowPresets" (click)="applyWindowPreset(preset)"
            [disabled]="!canEdit"
            class="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-bold hover:bg-primary/10 hover:text-primary transition-all disabled:opacity-50">
            {{ preset }}
          </button>
        </div>
        <span *ngIf="bookingForm.get('booking_window_days')?.invalid && bookingForm.get('booking_window_days')?.touched"
          class="text-xs text-rose-500 font-medium">
          {{ 'booking_settings_window_error' | translate }}
        </span>
        <span *ngIf="!(bookingForm.get('booking_window_days')?.invalid && bookingForm.get('booking_window_days')?.touched)"
          class="text-xs text-slate-400 font-medium">
          {{ 'booking_settings_window_hint' | translate }}
        </span>
      </div>

      <!-- Reschedule cutoff -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-semibold text-slate-500">
          {{ 'booking_settings_cutoff_label' | translate }}<span class="text-rose-500 ml-0.5">*</span>
        </label>
        <input type="number" pInputText formControlName="reschedule_cutoff_hours" min="0" max="720"
          class="!w-full !max-w-[200px] !rounded-xl !border-slate-200 !px-4 !py-3 !text-sm focus:!border-primary transition-all shadow-none h-[48px]">
        <div class="flex items-center gap-2 pt-1">
          <button type="button" *ngFor="let preset of rescheduleCutoffPresets" (click)="applyCutoffPreset(preset)"
            [disabled]="!canEdit"
            class="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-bold hover:bg-primary/10 hover:text-primary transition-all disabled:opacity-50">
            {{ preset }}
          </button>
        </div>
        <span *ngIf="bookingForm.get('reschedule_cutoff_hours')?.invalid && bookingForm.get('reschedule_cutoff_hours')?.touched"
          class="text-xs text-rose-500 font-medium">
          {{ 'booking_settings_cutoff_error' | translate }}
        </span>
        <span *ngIf="!(bookingForm.get('reschedule_cutoff_hours')?.invalid && bookingForm.get('reschedule_cutoff_hours')?.touched)"
          class="text-xs text-slate-400 font-medium">
          {{ 'booking_settings_cutoff_hint' | translate }}
        </span>
      </div>
    </fieldset>
  </div>

  <!-- Footer — sticks to the bottom of the viewport while the page scrolls,
       then scrolls away normally once the end of the form is reached. -->
  <div class="sticky bottom-0 z-10 flex justify-end items-center px-6 py-4 border-t border-slate-100 bg-white/95 backdrop-blur-sm shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
    <app-button *hasPermission="'Configurações:edit'" [label]="'common_save' | translate" type="submit" [loading]="isSaving()"
      [disabled]="bookingForm.invalid || isSaving()" styleClass="min-w-[180px]"></app-button>
  </div>
</form>

<!-- ── Public booking link ──────────────────────────────────────────────────
     A per-branch public URL anyone can use: a QR code in reception, the
     clinic's website, an Instagram bio. Deliberately OUTSIDE the settings
     <form> above — these write through an RPC, not through the settings row,
     so they must not ride on that form's Save button. -->
<section *ngIf="!isLoading()" class="flex flex-col gap-6 px-6 py-8 border-t border-slate-100 animate-fadeIn">

  <div class="flex flex-wrap items-start justify-between gap-4 max-w-4xl">
    <div class="flex flex-col gap-1">
      <h3 class="text-xl font-bold text-slate-700 tracking-tight">{{ 'public_booking_link_title' | translate }}</h3>
      <p class="text-sm text-slate-400 font-medium">{{ 'public_booking_link_subtitle' | translate }}</p>
    </div>
    <app-button *hasPermission="'Configurações:edit'"
      [label]="(showCreateLink() ? 'common_cancel' : 'public_booking_link_create') | translate"
      [icon]="showCreateLink() ? 'pi pi-times' : 'pi pi-plus'"
      [variant]="showCreateLink() ? 'outline' : 'primary'"
      [disabled]="activeLinkCount() >= maxActiveLinks && !showCreateLink()"
      (onClick)="toggleCreateLink()"></app-button>
  </div>

  <!-- Rotate-don't-deactivate guidance. This is the one piece of copy on the
       screen that prevents a real, expensive mistake: a retired slug answers
       with a neutral "invalid" page carrying NO phone number (deliberately —
       it must not confirm whether a slug ever existed), so a dead QR on 500
       printed flyers is a dead end for every single person who scans it. -->
  <div class="flex items-start gap-3 max-w-4xl px-4 py-3 rounded-2xl bg-amber-50 border border-amber-200">
    <i class="pi pi-exclamation-triangle text-amber-500 !text-sm mt-0.5 shrink-0"></i>
    <div class="flex flex-col gap-1">
      <p class="text-xs font-black text-amber-800 uppercase tracking-wider">
        {{ 'public_booking_link_printed_warning_title' | translate }}
      </p>
      <p class="text-xs font-medium text-amber-800/80 leading-relaxed">
        {{ 'public_booking_link_printed_warning_body' | translate }}
      </p>
    </div>
  </div>

  <!-- The booking page is served by the CRM app, not by this one, so its origin
       has to be configured. Blank = say so loudly rather than hand staff a
       half-formed address that looks copyable. -->
  <div *ngIf="!isCrmAppUrlConfigured"
    class="flex items-start gap-3 max-w-4xl px-4 py-3 rounded-2xl bg-rose-50 border border-rose-200">
    <i class="pi pi-ban text-rose-500 !text-sm mt-0.5 shrink-0"></i>
    <p class="text-xs font-medium text-rose-800 leading-relaxed">
      {{ 'public_booking_link_missing_base_url' | translate }}
    </p>
  </div>

  <!-- Mint form. A div, not a <form>: nesting one inside the settings form
       above would be invalid HTML, and Angular binds [formGroup] to any
       element. The button is type="button" for the same reason. -->
  <div *ngIf="showCreateLink()" [formGroup]="createLinkForm"
    class="flex flex-col gap-4 max-w-4xl p-5 rounded-2xl bg-slate-50 border border-slate-100">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="flex flex-col gap-1.5">
        <label for="publicLinkLabel" class="text-sm font-semibold text-slate-500">
          {{ 'public_booking_link_label_label' | translate }}
        </label>
        <input id="publicLinkLabel" type="text" pInputText formControlName="label" maxlength="120"
          [placeholder]="'public_booking_link_label_placeholder' | translate"
          class="!w-full !rounded-xl !border-slate-200 !px-4 !py-3 !text-sm focus:!border-primary transition-all shadow-none h-[48px]">
        <span class="text-xs text-slate-400 font-medium">{{ 'public_booking_link_label_hint' | translate }}</span>
      </div>

      <div class="flex flex-col gap-1.5">
        <label for="publicLinkDoctor" class="text-sm font-semibold text-slate-500">
          {{ 'public_booking_link_doctor_label' | translate }}
        </label>
        <p-select inputId="publicLinkDoctor" formControlName="doctor_id" [options]="doctorOptions()"
          optionLabel="name" optionValue="id" [filter]="true" filterBy="name" [showClear]="true" appendTo="body"
          [placeholder]="'public_booking_link_any_doctor' | translate"
          styleClass="!w-full !rounded-xl !border-slate-200 !text-sm">
        </p-select>
        <span class="text-xs text-slate-400 font-medium">{{ 'public_booking_link_doctor_hint' | translate }}</span>
      </div>
    </div>

    <div class="flex justify-end">
      <app-button type="button" [label]="'public_booking_link_create_submit' | translate"
        [loading]="isCreatingLink()" [disabled]="createLinkForm.invalid || isCreatingLink()"
        (onClick)="createLink()"></app-button>
    </div>
  </div>

  <!-- Loading -->
  <div *ngIf="isLoadingLinks()" class="flex items-center gap-3 py-8">
    <i class="pi pi-spinner pi-spin text-xl text-primary"></i>
    <p class="text-sm font-bold text-slate-500 tracking-tight">{{ 'common_loading' | translate }}...</p>
  </div>

  <!-- Empty -->
  <div *ngIf="!isLoadingLinks() && links().length === 0"
    class="flex flex-col items-center justify-center gap-3 max-w-4xl py-12 text-center rounded-2xl border border-dashed border-slate-200">
    <div class="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center">
      <i class="pi pi-qrcode text-2xl text-slate-300"></i>
    </div>
    <p class="text-sm font-bold text-slate-500">{{ 'public_booking_link_empty' | translate }}</p>
  </div>

  <!-- List -->
  <div *ngIf="!isLoadingLinks() && links().length > 0" class="flex flex-col gap-3 max-w-4xl">
    <div *ngFor="let link of links()"
      class="flex flex-col gap-4 p-5 rounded-2xl border transition-colors"
      [class.border-slate-100]="link.is_active"
      [class.bg-white]="link.is_active"
      [class.border-slate-200]="!link.is_active"
      [class.bg-slate-50]="!link.is_active">

      <div class="flex flex-wrap items-start justify-between gap-3">
        <div class="flex flex-col gap-1.5 min-w-0">
          <div class="flex items-center gap-2 flex-wrap">
            <h4 class="text-sm font-black tracking-tight truncate"
              [class.text-slate-800]="link.is_active" [class.text-slate-400]="!link.is_active">
              {{ link.label || ('public_booking_link_untitled' | translate) }}
            </h4>
            <span class="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md shrink-0"
              [class.text-emerald-700]="link.is_active" [class.bg-emerald-50]="link.is_active"
              [class.text-slate-400]="!link.is_active" [class.bg-slate-100]="!link.is_active">
              {{ (link.is_active ? 'public_booking_link_state_active' : 'public_booking_link_state_inactive') | translate }}
            </span>
          </div>
          <div class="flex items-center gap-3 flex-wrap text-xs font-semibold text-slate-400">
            <span class="inline-flex items-center gap-1.5">
              <i class="pi pi-user !text-[11px]"></i>{{ doctorName(link.doctor_id) }}
            </span>
            <span class="inline-flex items-center gap-1.5">
              <i class="pi pi-calendar !text-[11px]"></i>{{ link.created_at | date: 'mediumDate' }}
            </span>
          </div>
        </div>

        <!-- Mutations, gated exactly as the rest of the tab is. Inactive links
             carry no actions: there is no reactivation, by design. -->
        <div *ngIf="link.is_active" class="flex items-center gap-2 shrink-0">
          <app-button *hasPermission="'Configurações:edit'" type="button"
            [label]="'public_booking_link_rotate' | translate" icon="pi pi-refresh" variant="outline" size="small"
            [loading]="rotatingId() === link.id"
            [disabled]="rotatingId() === link.id || deactivatingId() === link.id"
            (onClick)="confirmRotate(link)"></app-button>
          <app-button *hasPermission="'Configurações:edit'" type="button"
            [label]="'public_booking_link_deactivate' | translate" icon="pi pi-power-off" variant="danger" size="small"
            [loading]="deactivatingId() === link.id"
            [disabled]="rotatingId() === link.id || deactivatingId() === link.id"
            (onClick)="confirmDeactivate(link)"></app-button>
        </div>
      </div>

      <!-- URL + copy + QR toggle -->
      <div class="flex items-center gap-2 px-4 py-2.5 rounded-xl border"
        [class.bg-slate-50]="link.is_active" [class.border-slate-100]="link.is_active"
        [class.bg-white]="!link.is_active" [class.border-slate-200]="!link.is_active">
        <span class="text-xs font-bold text-slate-600 truncate flex-1" [title]="linkUrl(link)">{{ linkUrl(link) }}</span>
        <button type="button" (click)="copyLink(link)" [disabled]="!isCrmAppUrlConfigured"
          [title]="'public_booking_link_copy' | translate"
          [attr.aria-label]="'public_booking_link_copy' | translate"
          class="w-9 h-9 shrink-0 rounded-xl bg-white border border-slate-100 shadow-sm flex items-center justify-center hover:bg-slate-100 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
          <i class="pi pi-copy text-sm text-primary"></i>
        </button>
        <button type="button" (click)="toggleQr(link)" [disabled]="!isCrmAppUrlConfigured"
          [title]="'public_booking_link_show_qr' | translate"
          [attr.aria-label]="'public_booking_link_show_qr' | translate"
          [attr.aria-expanded]="expandedQrId() === link.id"
          class="w-9 h-9 shrink-0 rounded-xl bg-white border border-slate-100 shadow-sm flex items-center justify-center hover:bg-slate-100 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
          <i class="pi pi-qrcode text-sm text-primary"></i>
        </button>
      </div>

      <!-- QR, rendered only on demand — 50 inline image fetches on page load
           would be absurd, and most rows are never scanned from this screen. -->
      <div *ngIf="expandedQrId() === link.id && isCrmAppUrlConfigured" class="flex flex-col items-center gap-3 py-2">
        <div class="w-44 h-44 bg-white rounded-2xl border-2 border-dashed border-slate-200 overflow-hidden flex items-center justify-center">
          <img [src]="qrUrl(link)" class="w-full h-full object-contain p-3"
            [alt]="('public_booking_link_qr_alt' | translate) + ' — ' + (link.label || ('public_booking_link_untitled' | translate))">
        </div>
        <p class="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">
          {{ 'public_booking_link_qr_hint' | translate }}
        </p>
      </div>
    </div>
  </div>
</section>

<p-confirmDialog #pblCd key="publicBookingLinkConfirm">
  <ng-template pTemplate="footer">
    <app-button *ngIf="pblCd.confirmation?.rejectVisible !== false"
      [label]="pblCd.confirmation?.rejectLabel || ('NO' | translate)" variant="outline"
      (onClick)="pblCd.onReject()"></app-button>
    <app-button [label]="pblCd.confirmation?.acceptLabel || ('YES' | translate)"
      (onClick)="pblCd.onAccept()"></app-button>
  </ng-template>
</p-confirmDialog>
